<template>
    <div
        data-interface-target="init_screen"
        :class="[$style.init_screen, !props.isFullscreen && $style.non_fullscreen]"
        @click="emit('click')"
    >
        <div
            :class="$style.background"
            :style="{ backgroundImage: `url(${props.game.preview})` }"
        />
        <div :class="$style.content">
            <ShortGameInfo
                :game="props.game"
                :category="category"
                placement="init_screen"
                :class="$style.short_info_override"
            />
            <div :class="$style.cover_image_and_play_button_wrapper">
                <img
                    :src="props.game.preview"
                    :alt="props.game.title"
                    :class="$style.cover_image"
                />
                <PlayButton
                    :label="$tr('gameContainer.init_button')"
                    :class="$style.play_button_override"
                    interfaceTarget="init_button"
                    withLoader
                />
            </div>
            <SeoText
                :text="seoText"
                :class="$style.seo_text_override"
            />
        </div>
    </div>
</template>
<script setup lang="ts">
import { computed } from 'vue'

import { OPPO_WL_CLID } from '@/constants/general'
import { useAppStore } from '@/store/app-store'
import type { APIGameType, GameCategoriesInfoType } from '@/types'

import { getMainCategory } from '../../utils'
import PlayButton from '../PlayButton.vue'
import ShortGameInfo from '../ShortGameInfo.vue'
import SeoText from './SeoText.vue'

const emit = defineEmits<{
    click: []
}>()

const props = defineProps<{
    game: APIGameType
    categories?: GameCategoriesInfoType
    isFullscreen: boolean
}>()

const appStore = useAppStore()

const isOppoWLClid = computed(() => appStore.clid === OPPO_WL_CLID)
const category = computed(() => (isOppoWLClid.value ? undefined : getMainCategory(props.game, props.categories)?.title))
const seoText = computed(() => (isOppoWLClid.value ? props.game.description : props.game.seo_text))
</script>
<style module>
.init_screen {
    width: 100%;
    height: 100%;
    background-color: var(--black);
    overflow: hidden;
}

.background {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    filter: blur(26px);
    opacity: 0.5;
}

.content {
    position: relative;
    z-index: 1;
    height: 100%;
    display: grid;
    grid-template-areas:
        'short-info'
        'cover-image-wrapper'
        'seo-text';
    gap: 12px;
    padding: 12px;
    box-sizing: border-box;
    overflow: hidden;
}

.cover_image_and_play_button_wrapper {
    grid-area: cover-image-wrapper;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.short_info_override {
    grid-area: short-info;
}

.seo_text_override {
    grid-area: seo-text;
}

.cover_image {
    width: 100%;
    border-radius: var(--radius-16);
    aspect-ratio: var(--thumbnail-aspect-ratio);
    object-fit: cover;
}

.short_info_override {
    padding-top: calc(20vh - 12px);
}

.non_fullscreen .short_info_override,
.non_fullscreen .seo_text_override {
    display: none;
}

@media (--mobile-landscape) {
    .content {
        grid-template-areas:
            'short-info'
            'cover-image-wrapper'
            'seo-text';
    }

    .cover_image_and_play_button_wrapper {
        flex-direction: row;
        padding: 16px 40px;
    }

    .cover_image,
    .play_button_override {
        width: 0;
        flex: 1 1 50%;
    }

    .play_button_override {
        height: 100%;
    }

    .play_button_override * {
        font-size: 24px; /* fallback for browsers that do not support container queries */
    }

    @supports (font-size: 1cqw) {
        .play_button_override * {
            font-size: clamp(12px, 12cqi, 28px);
        }
    }

    .short_info_override {
        padding-top: 0;
        justify-self: end;
    }

    .short_info_override div {
        align-items: flex-end;
    }

    .short_info_override img {
        display: none;
    }

    .seo_text_override {
        padding: 0 40px;
    }
}

@media (--tablet-plus) {
    .background {
        mask-image:
            linear-gradient(to right, transparent 0%, black 20%, black 80%, transparent 100%),
            linear-gradient(to bottom, transparent 0%, black 20%, black 80%, transparent 100%);
        mask-composite: intersect;
    }

    .content {
        grid-template-columns: 1fr minmax(0, 560px) 1fr;
        grid-template-areas:
            '. short-info .'
            '. cover-image-wrapper .'
            '. seo-text .';
    }

    .short_info_override {
        padding-top: 0;
    }

    .short_info_override img {
        display: none;
    }
}
</style>
